import React, { createContext, useContext, useRef, useState } from 'react';

import {
  GetMeDocument,
  WebTutorialFlow,
  useCompleteWebTutorialFlowsMutation,
} from '@graphql/generated';
import { useChatTutorial } from '@hooks/useChatTutorial';
import useMe from '@hooks/useMe';
import { MIXPANEL_EVENTS } from '@src/constants/constants';
import { useMixpanel } from '@src/utils/mixpanel';
import { AsyncStorage } from '@utils/storage';

type TutorialContextType = {
  isVisible: boolean;
  headerOverlayActive: boolean;
  recentTaskCreated: string | undefined;
  setRecentTaskCreated: React.Dispatch<
    React.SetStateAction<string | undefined>
  >;
  setHeaderOverlayActive: React.Dispatch<React.SetStateAction<boolean>>;
  setIsVisible: React.Dispatch<React.SetStateAction<boolean>>;
  currentStep: number;
  setCurrentStep: React.Dispatch<React.SetStateAction<number>>;
  startTutorial: () => void;
  nextStep: () => void;
  previousStep: () => void;
  closeTutorial: () => void;
  setProjectTutorialStateForUser: (value: string) => Promise<void>;
  projectTutorialState: string | null;
  addMembersTutorialState: string | null;
  goToStep: (stepIndex: number) => void;
  setProjectTutorialStateAndInStorage: (state: string) => void;
  setAddMemeberTutorialStateAndInStorage: (state: string) => void;
};

const ProjectTutorialContext = createContext<TutorialContextType | undefined>(
  undefined
);

export const ProjectTutorialProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [isVisible, setIsVisible] = useState(false);
  const { me } = useMe();
  const { trackEvent } = useMixpanel();
  const hasTrackedOnboardingStart = useRef(false);
  const { chatTutorialState, isVisibleForChat, setIsVisibleForChat } =
    useChatTutorial();
  const [completeWebTutorialFlows] = useCompleteWebTutorialFlowsMutation({
    refetchQueries: [{ query: GetMeDocument }, 'getMe'],
  });

  const [currentStep, setCurrentStep] = useState(0);
  const [headerOverlayActive, setHeaderOverlayActive] =
    useState<boolean>(false);
  const [addMembersTutorialState, setAddMemebersTutorialState] = useState('1');

  const [projectTutorialState, setProjectTutorialState] = useState<
    string | null
  >('');

  const [recentTaskCreated, setRecentTaskCreated] = useState<
    string | undefined
  >(undefined);

  const projectTutorialStateCheckNew = async (state: string) => {
    if (
      state === '6' &&
      !me?.completedWebTutorialFlows?.includes('project_creation')
    ) {
      return WebTutorialFlow.ProjectCreation;
    }

    if (state === '15' && addMembersTutorialState === '4') {
      isVisible && setIsVisible(false);
    }

    if (state === '14') {
      await AsyncStorage.setItem('isProjectTutorializationCompleted', 'true');
      // isVisible && setIsVisible(false);
      if (chatTutorialState === '9') {
        isVisibleForChat && setIsVisibleForChat(false);
        await AsyncStorage.deleteItem('new_user');
      }
      if (!me?.completedWebTutorialFlows?.includes('task_creation'))
        return WebTutorialFlow.TaskCreation;
    }
    return null;
  };

  const setProjectTutorialStateAndInStorage = async (state: string) => {
    const previousState = projectTutorialState;
    await AsyncStorage.setItem('projectTutorialState', state);

    const updateProgress = await projectTutorialStateCheckNew(state);

    setProjectTutorialState(state);

    if (previousState !== state) {
      if (
        !hasTrackedOnboardingStart.current &&
        (previousState === '' || previousState === null) &&
        state === '0' &&
        (!me?.completedWebTutorialFlows ||
          me.completedWebTutorialFlows.length === 0)
      ) {
        hasTrackedOnboardingStart.current = true;
        trackEvent(MIXPANEL_EVENTS.ONBOARDING_STARTED, {
          platform: 'mobile',
          tutorial_type: 'project',
        });
      }

      trackEvent(MIXPANEL_EVENTS.ONBOARDING_STEP_COMPLETED, {
        platform: 'mobile',
        tutorial_type: 'project',
        from_state: previousState,
        to_state: state,
      });

      if (state === '14') {
        trackEvent(MIXPANEL_EVENTS.ONBOARDING_COMPLETED, {
          platform: 'mobile',
          tutorial_type: 'project',
          final_state: state,
        });
      }
    }

    if (updateProgress) {
      completeWebTutorialFlows({
        variables: {
          flowType: updateProgress,
        },
      });
    }
  };

  const setAddMemeberTutorialStateAndInStorage = async (
    state: string,
    update?: boolean
  ) => {
    const previousState = addMembersTutorialState;
    await AsyncStorage.setItem('addMembersTutorialState', state);

    if (previousState !== state) {
      trackEvent(MIXPANEL_EVENTS.ONBOARDING_STEP_COMPLETED, {
        platform: 'mobile',
        tutorial_type: 'add_members',
        from_state: previousState,
        to_state: state,
      });

      if (state === '4') {
        trackEvent(MIXPANEL_EVENTS.ONBOARDING_COMPLETED, {
          platform: 'mobile',
          tutorial_type: 'add_members',
          final_state: state,
        });
      }
    }

    if (state === '4') {
      if (projectTutorialState === '15') {
        isVisible && setIsVisible(false);
      }
      update !== false &&
        completeWebTutorialFlows({
          variables: {
            flowType: WebTutorialFlow.InviteMember,
          },
        });
    }
    setAddMemebersTutorialState(state);
  };

  const startTutorial = () => {
    setCurrentStep(0);
  };

  const nextStep = () => {
    setCurrentStep((step) => step + 1);
  };

  const previousStep = () => {
    setCurrentStep((step) => Math.max(step - 1, 0));
  };

  const closeTutorial = () => {
    setCurrentStep(0);
  };

  const goToStep = (stepIndex: number) => {
    if (stepIndex >= 0) setCurrentStep(stepIndex);
  };

  const checkWebTutorialCompleted = () => {
    return (
      me?.completedWebTutorialFlows?.includes('project_creation') &&
      me?.completedWebTutorialFlows?.includes('task_creation') &&
      me?.completedWebTutorialFlows?.includes('invite_member') &&
      me?.completedWebTutorialFlows?.includes('chat') &&
      (me?.completedWebTutorialFlows?.includes('tag_on_chat_panel_open') ||
        me?.completedWebTutorialFlows?.includes('tag_on_chat_panel_closed'))
    );
  };

  const setProjectTutorialStateForUser = async (_value: string) => {
    // Check both context state and AsyncStorage to avoid race conditions
    if (projectTutorialState === '14' || projectTutorialState === '15') {
      setIsVisible(false);
      return;
    }

    try {
      const isProjectTutorializationCompleted = await AsyncStorage.getItem(
        'isProjectTutorializationCompleted'
      );
      if (isProjectTutorializationCompleted === 'true') {
        setIsVisible(false);
        return;
      }
    } catch {
      // Storage read failed — continue with tutorial check
    }

    const webTutorialCompleted = checkWebTutorialCompleted();
    if (webTutorialCompleted) {
      setIsVisible(false);
      return;
    }

    setIsVisible(true);

    const webProjectCompleted =
      me?.completedWebTutorialFlows?.includes('project_creation');

    const webTaskCompleted =
      me?.completedWebTutorialFlows?.includes('task_creation');

    const webInviteMemberCompleted =
      me?.completedWebTutorialFlows?.includes('invite_member');

    if (webTaskCompleted) {
      setProjectTutorialStateAndInStorage('15');
    } else if (webProjectCompleted) {
      projectTutorialState !== '6' && setProjectTutorialStateAndInStorage('7');
    } else {
      setProjectTutorialStateAndInStorage('0');
    }
    setTimeout(() => {
      if (
        webInviteMemberCompleted &&
        (webTaskCompleted || webProjectCompleted)
      ) {
        setAddMemeberTutorialStateAndInStorage('4', false);
      } else {
        setAddMemeberTutorialStateAndInStorage('1');
      }
    }, 500);
  };

  return (
    <ProjectTutorialContext.Provider
      value={{
        isVisible,
        recentTaskCreated,
        setRecentTaskCreated,
        headerOverlayActive,
        setHeaderOverlayActive,
        addMembersTutorialState,
        setAddMemeberTutorialStateAndInStorage,
        setIsVisible,
        currentStep,
        setCurrentStep,
        startTutorial,
        nextStep,
        previousStep,
        closeTutorial,
        setProjectTutorialStateAndInStorage,
        goToStep,
        projectTutorialState,
        setProjectTutorialStateForUser,
      }}>
      {children}
    </ProjectTutorialContext.Provider>
  );
};

// Hook to use context
export function useProjectTutorial() {
  const context = useContext(ProjectTutorialContext);
  if (context === undefined) {
    throw new Error(
      'useProjectTutorial must be used within a ProjectTutorialProvider'
    );
  }
  return context;
}
